/**
 * i18n bootstrap for the web client (backend docs/RFC_I18N.md).
 *
 * - Catalog format: i18next JSON v4 (nested keys, `{{var}}`, CLDR plural suffixes).
 * - Own UI namespaces are bundled: `src/i18n/locales/<locale>/<ns>.json`.
 * - Shared namespaces (`taxa`, `catalog`, `sim`, `errors`, `scenarios`) come
 *   from `GET /api/i18n/:locale/:ns`, already merged with the fallback chain.
 * - Fallback chain: requested (`es-MX`) → base (`es`) → `en` → the key itself.
 *
 * No language is enumerated here: the bundle's locale folders and the backend
 * manifest are the only lists.
 */
import { useEffect, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import {
  bundledLocales, bundledNamespaces, loadBundledCatalog, loadLocaleManifest, loadSharedCatalog,
} from './catalogSource';
import { currentLocale } from './format';

export {
  DATE_STYLES, formatDate, formatDateTime, formatNumber, formatList, monthName, currentLocale,
  datePickerFormat,
} from './format';
export type { DateStyle } from './format';
export { bundledLocales, bundledNamespaces };

export const SOURCE_LOCALE = 'en';
export const LOCALE_STORAGE_KEY = 'canifole.locale';
export const SHARED_NAMESPACES = ['taxa', 'catalog', 'sim', 'errors', 'scenarios'] as const;
export type SharedNamespace = typeof SHARED_NAMESPACES[number];

const isSharedNamespace = (ns: string): ns is SharedNamespace =>
  (SHARED_NAMESPACES as readonly string[]).includes(ns);

type Catalog = Record<string, unknown>;

const isPromise = <T,>(value: unknown): value is Promise<T> =>
  !!value && typeof (value as Promise<T>).then === 'function';

/** i18next backend: bundled JSON for own namespaces, HTTP for shared ones. */
const catalogBackend = {
  type: 'backend' as const,
  init() {},
  read(locale: string, ns: string, callback: (err: unknown, data: Catalog | false) => void) {
    const result: Catalog | Promise<Catalog> = isSharedNamespace(ns)
      ? loadSharedCatalog(locale, ns)
      : loadBundledCatalog(locale, ns);
    if (isPromise<Catalog>(result)) {
      result.then((data) => callback(null, data), (err) => callback(err, false));
    } else {
      callback(null, result);
    }
  },
};

const safeStorage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Private mode or blocked storage: the choice lives for this tab only.
    }
  },
};

const canonical = (tag: string): string | null => {
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null;
  } catch {
    return null;
  }
};

/** Exact match, then base language (`es-MX` → `es`), among `available`. */
export const matchLocale = (candidate: string, available: readonly string[]): string | null => {
  const tag = canonical(candidate);
  if (!tag) return null;
  const lower = tag.toLowerCase();
  const exact = available.find((code) => code.toLowerCase() === lower);
  if (exact) return exact;
  const base = lower.split('-')[0];
  return available.find((code) => code.toLowerCase() === base) ?? null;
};

/**
 * Saved user choice → `navigator.languages` matched against bundled locales → `en`.
 * A saved choice is honoured even without a bundled folder: the backend may
 * serve that language, and own namespaces then fall back to `en`.
 */
export const detectLocale = (): string => {
  const saved = safeStorage.get(LOCALE_STORAGE_KEY);
  if (saved) {
    const tag = matchLocale(saved, bundledLocales) ?? canonical(saved);
    if (tag) return tag;
  }
  const preferred = typeof navigator !== 'undefined'
    ? (navigator.languages?.length ? navigator.languages : [navigator.language])
    : [];
  for (const candidate of preferred) {
    if (!candidate) continue;
    const match = matchLocale(candidate, bundledLocales);
    if (match) return match;
  }
  return SOURCE_LOCALE;
};

const syncHtmlLang = (locale: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
};

i18n.on('languageChanged', syncHtmlLang);

i18n
  .use(catalogBackend)
  .use(initReactI18next)
  .init({
    lng: detectLocale(),
    fallbackLng: SOURCE_LOCALE,
    load: 'all',
    // All bundled namespaces of the current language up front: utils outside
    // React call i18n.t('<ns>:…') and must not depend on which screen loaded first.
    ns: bundledNamespaces,
    defaultNS: 'common',
    fallbackNS: false,
    returnEmptyString: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
    // The jest catalog source answers synchronously; let init finish before
    // the first render there. In the browser loading is async anyway.
    initAsync: false,
  })
  .then(() => {
    syncHtmlLang(currentLocale());
    // Error toasts read `errors:<errorCode>`; warm it up without blocking.
    void i18n.loadNamespaces('errors');
  });

/** Switches the UI language and remembers the choice. */
export const changeLanguage = (locale: string) => {
  safeStorage.set(LOCALE_STORAGE_KEY, locale);
  return i18n.changeLanguage(locale);
};

/** Current UI language; re-renders on change. */
export const useLocale = (): string => {
  const { i18n: instance } = useTranslation();
  return instance.language || instance.resolvedLanguage || SOURCE_LOCALE;
};

/** Value for the `Accept-Language` header of API requests. */
export const acceptLanguage = (): string => {
  const locale = currentLocale();
  const base = locale.split('-')[0];
  if (locale === SOURCE_LOCALE) return locale;
  return base !== locale && base !== SOURCE_LOCALE
    ? `${locale}, ${base};q=0.9, ${SOURCE_LOCALE};q=0.8`
    : `${locale}, ${SOURCE_LOCALE};q=0.8`;
};

export interface LocaleInfo {
  code: string;
  name: string;
  nativeName: string;
}

const displayName = (code: string, inLocale: string): string => {
  try {
    const name = new Intl.DisplayNames([inLocale], { type: 'language' }).of(code) ?? code;
    return name.charAt(0).toLocaleUpperCase(inLocale) + name.slice(1);
  } catch {
    return code;
  }
};

/** Locale list from the bundle folders, used until/unless the manifest answers. */
export const bundledLocaleInfos = (): LocaleInfo[] => bundledLocales.map((code) => ({
  code,
  name: displayName(code, SOURCE_LOCALE),
  nativeName: displayName(code, code),
}));

let manifestRequest: Promise<LocaleInfo[]> | null = null;

/** `GET /api/i18n/locales`, falling back to the bundled folders. Cached per page load. */
export const fetchLocaleManifest = (): Promise<LocaleInfo[]> => {
  if (!manifestRequest) {
    manifestRequest = loadLocaleManifest()
      .then((body: { locales?: Array<Partial<LocaleInfo>> } | null) => {
        const locales = (body?.locales || [])
          .filter((l): l is Partial<LocaleInfo> & { code: string } => typeof l?.code === 'string' && !!l.code)
          .map((l) => ({
            code: l.code,
            name: l.name || displayName(l.code, SOURCE_LOCALE),
            nativeName: l.nativeName || displayName(l.code, l.code),
          }));
        return locales.length ? locales : bundledLocaleInfos();
      })
      .catch(() => bundledLocaleInfos());
  }
  return manifestRequest;
};

/** Languages offered in the switcher. */
export const useAvailableLocales = (): LocaleInfo[] => {
  const [locales, setLocales] = useState<LocaleInfo[]>(bundledLocaleInfos);
  useEffect(() => {
    let alive = true;
    fetchLocaleManifest().then((list) => {
      if (alive) setLocales(list);
    });
    return () => {
      alive = false;
    };
  }, []);
  return locales;
};

export default i18n;
